import { cn } from "@/utils";
import { ViewportPortal } from "@xyflow/react";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import { useQuantizedZoom } from "../hooks/useQuantizedZoom";

/** Constant screen size of a canvas pin bubble — chat and comment share it. */
export const PIN_ICON_SIZE = 40;
/** Screen-space gap between the bubble's right edge and the panel's left edge. */
export const PIN_ICON_GAP = 10;
/**
 * Stacking rung for a `ViewportPortal` pin layer. Every node paints below it:
 * React Flow adds 1000 to the z of a selected node, and it selects the node it
 * is dragging, so a pin without this vanishes under whatever is being moved.
 * Under the panel's 9999, which stays the topmost canvas surface.
 */
export const PIN_LAYER_Z_INDEX = 2000;

/**
 * The geometry every canvas pin wears: a bubble held at a constant screen
 * size by an inverse-zoom scale, and an optional panel floated beside it in
 * canvas space. Ownership, persistence, contents and permissions stay with
 * the caller — this owns only the two transforms and their separation.
 *
 * The bubble renders in place, so the caller positions it: a React Flow node
 * body already sits at the node's origin, and the comment pin layer wraps it
 * in an absolutely-positioned div inside its own ViewportPortal.
 */
export function CanvasPinAffordance({
  x,
  y,
  open,
  centered,
  bubble,
  bubbleClassName,
  bubbleTitle,
  panel,
  panelWidth,
  panelHeight,
  panelKey,
  panelOffset,
  onBubbleClick,
}: {
  /** Absolute world position the panel is placed against. */
  x: number;
  y: number;
  open: boolean;
  /** (x, y) is the bubble's center rather than its top-left. */
  centered?: boolean;
  bubble?: ReactNode;
  bubbleClassName?: string;
  bubbleTitle?: string;
  /** The two-layer ViewportPortal body. Omit for a bubble-only affordance. */
  panel?: ReactNode;
  panelWidth?: number;
  panelHeight?: number;
  panelKey?: string;
  /** Screen-px displacement of the panel from the bubble, for a dragged window. */
  panelOffset?: { x: number; y: number };
  onBubbleClick?: (e: React.MouseEvent) => void;
}) {
  const zoom = useQuantizedZoom();
  const inverseScale = 1 / zoom;

  // A comment pin marks a point, so its bubble straddles (x, y); a node body
  // starts there. The shift rides inside the counter-scaled transform, which
  // keeps it 20 screen-px at every zoom.
  const shift = centered ? -PIN_ICON_SIZE / 2 : 0;

  // Panel position in flow coordinates. Its left edge sits
  // (PIN_ICON_SIZE + PIN_ICON_GAP) screen-px right of the bubble origin, and
  // screen pixels are flow pixels × zoom, so the offset is pixels / zoom.
  const panelLeft =
    x + (PIN_ICON_SIZE + PIN_ICON_GAP + shift + (panelOffset?.x ?? 0)) / zoom;
  const panelTop = y + (shift + (panelOffset?.y ?? 0)) / zoom;

  return (
    <>
      {bubble && (
        <div
          className={cn("origin-top-left", bubbleClassName)}
          style={{
            transform: shift
              ? `scale(${inverseScale}) translate(${shift}px, ${shift}px)`
              : `scale(${inverseScale})`,
          }}
          onClick={onBubbleClick}
          title={bubbleTitle}
        >
          {bubble}
        </div>
      )}

      {/* Panel — inside ViewportPortal so it lives in canvas space.
          This means it automatically follows pan/zoom and icon drags.
          Two-layer approach:
            outer motion.div → flow-space positioning + zoom compensation
            inner motion.div → enter/exit animation (scale + opacity spring)
          Keeping them separate avoids conflicts between Motion's transform and
          the zoom-compensation transform on the same element. */}
      {panel && (
        <ViewportPortal>
          <AnimatePresence>
            {open && (
              // Outer motion.div: tracked by AnimatePresence (handles opacity
              // exit) + zoom-compensation transform. Keeping zoom-compensation
              // here means the inner motion.div's scale/y operate in
              // screen-space coordinates.
              <motion.div
                key={panelKey ?? "panel"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
                style={{
                  position: "absolute",
                  left: panelLeft,
                  top: panelTop,
                  transform: `scale(${inverseScale})`,
                  transformOrigin: "top left",
                  width: panelWidth,
                  height: panelHeight,
                  zIndex: 9999,
                  pointerEvents: "auto",
                }}
              >
                {/* Inner motion.div: scale + y spring — no transform conflict */}
                <motion.div
                  initial={{ scale: 0.92, y: -6 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.92, y: -6 }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  style={{
                    transformOrigin: "top left",
                    width: "100%",
                    height: panelHeight ? "100%" : undefined,
                  }}
                >
                  {panel}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </ViewportPortal>
      )}
    </>
  );
}
